import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { PageHeader } from '@/components/layout/PageHeader'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { Select } from '@/components/ui/Select'
import { createClient } from '@/api/supabase'
import { USE_MOCK } from '@/config'
import { NOTIFICATION_ROLES, notificationRoleLabel } from './i18n/notification_roles'
import { UserSearchInput } from './components/UserSearchInput'
import { useBroadcast } from './hooks/useBroadcast'
import type { UserChip } from './components/UserSearchInput'

type AudienceMode = 'roles' | 'users' | 'roles_and_users'

interface Toast {
  type: 'success' | 'error'
  message: string
  broadcastId?: string
}

async function countRecipients(roles: string[], userIds: string[]): Promise<number> {
  if (USE_MOCK || (roles.length === 0 && userIds.length === 0)) return 0
  const supabase = createClient()
  let q = supabase.from('profiles').select('id').or('is_active.is.null,is_active.eq.true')
  if (roles.length > 0 && userIds.length > 0) {
    q = q.or(`role.in.(${roles.join(',')}),id.in.(${userIds.join(',')})`)
  } else if (roles.length > 0) {
    q = q.in('role', roles)
  } else {
    q = q.in('id', userIds)
  }
  const { data, error } = await q.select('id')
  if (error) return 0
  return new Set((data ?? []).map((r) => r.id as string)).size
}

export function BroadcastPage() {
  const { t } = useTranslation()
  const audienceModeOptions: { value: AudienceMode; label: string }[] = [
    { value: 'roles', label: t('notif.broadcast.audienceRoles') },
    { value: 'users', label: t('notif.broadcast.audienceUsers') },
    { value: 'roles_and_users', label: t('notif.broadcast.audienceRolesAndUsers') },
  ]
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [audienceMode, setAudienceMode] = useState<AudienceMode>('roles')
  const [selectedRoles, setSelectedRoles] = useState<Set<string>>(new Set())
  const [chips, setChips] = useState<UserChip[]>([])
  const [recipientCount, setRecipientCount] = useState(0)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [toast, setToast] = useState<Toast | null>(null)

  const broadcastMut = useBroadcast()

  const chipIds = useMemo(() => chips.map((c) => c.id), [chips])
  const roles = useMemo(() => [...selectedRoles], [selectedRoles])

  const rolesForCount = audienceMode === 'users' ? [] : roles
  const userIdsForCount = audienceMode === 'roles' ? [] : chipIds

  // Recalculate recipient preview
  useEffect(() => {
    let cancelled = false
    void countRecipients(rolesForCount, userIdsForCount).then((n) => {
      if (!cancelled) setRecipientCount(n)
    })
    return () => { cancelled = true }
  }, [rolesForCount, userIdsForCount])

  function toggleRole(role: string) {
    setSelectedRoles((prev) => {
      const next = new Set(prev)
      if (next.has(role)) next.delete(role)
      else next.add(role)
      return next
    })
  }

  function addChip(chip: UserChip) {
    setChips((prev) => {
      if (prev.some((c) => c.id === chip.id)) return prev
      return [...prev, chip]
    })
  }

  function removeChip(id: string) {
    setChips((prev) => prev.filter((c) => c.id !== id))
  }

  const canSend = title.trim() && body.trim() && recipientCount > 0 && !broadcastMut.isPending

  async function handleConfirm() {
    setConfirmOpen(false)
    try {
      const result = await broadcastMut.mutateAsync({
        title: title.trim(),
        body: body.trim(),
        roles: audienceMode === 'users' ? [] : roles,
        user_ids: audienceMode === 'roles' ? [] : chipIds,
      })
      setToast({
        type: 'success',
        message: t('notif.broadcast.sentToast', { sent: result.sent, recipients: result.recipients }),
        broadcastId: result.broadcast_id,
      })
      setTitle('')
      setBody('')
      setSelectedRoles(new Set())
      setChips([])
    } catch (e) {
      setToast({
        type: 'error',
        message: e instanceof Error ? e.message : t('notif.broadcast.sendError'),
      })
    }
    setTimeout(() => setToast(null), 6000)
  }

  return (
    <div>
      <PageHeader title={t('notif.broadcast.title')} />

      {toast && (
        <div
          role="status"
          className={`fixed bottom-6 right-6 z-[100] max-w-sm rounded-lg border px-4 py-3 text-sm shadow-lg ${
            toast.type === 'success'
              ? 'border-emerald-700 bg-emerald-950/80 text-emerald-100'
              : 'border-[color:var(--stryzh-color-status-error)] bg-[color:var(--stryzh-color-surface)] text-[color:var(--stryzh-color-status-error)]'
          }`}
        >
          <p>{toast.message}</p>
          {toast.broadcastId ? (
            <Link
              to={`/notifications/broadcasts?highlight=${encodeURIComponent(toast.broadcastId)}`}
              className="mt-1 block text-xs underline opacity-80"
            >
              {t('notif.broadcast.historyLink')}
            </Link>
          ) : null}
        </div>
      )}

      <div className="mx-auto max-w-2xl space-y-6">
        {/* Message */}
        <div className="rounded-xl border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-surface)] p-4 space-y-3">
          <h2 className="text-sm font-semibold text-[color:var(--stryzh-color-text-primary)]">{t('notif.broadcast.message')}</h2>
          <div>
            <label className="mb-1 block text-xs text-[color:var(--stryzh-color-text-secondary)]">
              {t('notif.broadcast.titleLabel')} <span className="text-[color:var(--stryzh-color-status-error)]">*</span>
            </label>
            <input
              type="text"
              value={title}
              maxLength={100}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('notif.broadcast.titlePlaceholder')}
              className="w-full rounded-[var(--stryzh-radius-lg)] border border-[color:var(--stryzh-color-border-default)] bg-transparent px-3 py-2 text-sm text-[color:var(--stryzh-color-text-primary)] placeholder:text-[color:var(--stryzh-color-text-secondary)] outline-none transition-colors focus:border-[var(--stryzh-color-accent-orange)]"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-[color:var(--stryzh-color-text-secondary)]">
              {t('notif.broadcast.textLabel')} <span className="text-[color:var(--stryzh-color-status-error)]">*</span>
            </label>
            <textarea
              value={body}
              maxLength={500}
              rows={4}
              onChange={(e) => setBody(e.target.value)}
              placeholder={t('notif.broadcast.bodyPlaceholder')}
              className="w-full rounded-[var(--stryzh-radius-lg)] border border-[color:var(--stryzh-color-border-default)] bg-transparent px-3 py-2 text-sm text-[color:var(--stryzh-color-text-primary)] placeholder:text-[color:var(--stryzh-color-text-secondary)] outline-none transition-colors focus:border-[var(--stryzh-color-accent-orange)]"
            />
          </div>
        </div>

        <div className="rounded-xl border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-surface)] p-4 space-y-3">
          <h2 className="text-sm font-semibold text-[color:var(--stryzh-color-text-primary)]">{t('notif.broadcast.audience')}</h2>
          <label className="flex flex-col gap-1">
            <span className="stryzh-input-label">{t('notif.broadcast.audienceType')}</span>
            <Select<AudienceMode>
              value={audienceMode}
              options={audienceModeOptions}
              onChange={(v) => {
                if (v === '') return
                setAudienceMode(v)
                if (v === 'roles') setChips([])
                if (v === 'users') setSelectedRoles(new Set())
              }}
            />
          </label>
        </div>

        {(audienceMode === 'roles' || audienceMode === 'roles_and_users') && (
          <div className="rounded-xl border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-surface)] p-4">
            <h2 className="mb-3 text-sm font-semibold text-[color:var(--stryzh-color-text-primary)]">
              {t('notif.broadcast.recipientRoles')}
            </h2>
            <div className="flex flex-wrap gap-2">
              {NOTIFICATION_ROLES.map((role) => (
                <button
                  key={role}
                  type="button"
                  className={clsx(
                    'stryzh-filter-chip',
                    selectedRoles.has(role) && 'stryzh-filter-chip--selected',
                  )}
                  onClick={() => toggleRole(role)}
                >
                  {notificationRoleLabel(role)}
                </button>
              ))}
            </div>
          </div>
        )}

        {(audienceMode === 'users' || audienceMode === 'roles_and_users') && (
          <div className="rounded-xl border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-surface)] p-4">
            <h2 className="mb-3 text-sm font-semibold text-[color:var(--stryzh-color-text-primary)]">
              {audienceMode === 'users' ? t('notif.broadcast.recipients') : t('notif.broadcast.extraRecipients')}
            </h2>
            <UserSearchInput chips={chips} onAdd={addChip} onRemove={removeChip} />
          </div>
        )}

        {/* Preview + send */}
        <div className="rounded-xl border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-surface)] p-4">
          <p className="mb-4 text-sm text-[color:var(--stryzh-color-text-secondary)]">
            {t('notif.broadcast.willSendPrefix')}{' '}
            <span className="font-semibold text-[color:var(--stryzh-color-text-primary)]">
              {recipientCount}
            </span>{' '}
            {t('notif.broadcast.willSendSuffix')}
          </p>
          <Button
            variant="primary"
            disabled={!canSend}
            onClick={() => setConfirmOpen(true)}
            className="w-full sm:w-auto"
          >
            {broadcastMut.isPending ? t('notif.broadcast.sending') : t('notif.broadcast.send')}
          </Button>
        </div>
      </div>

      {confirmOpen && (
        <Modal
          title={t('notif.broadcast.confirmTitle', { count: recipientCount })}
          onClose={() => setConfirmOpen(false)}
          footer={
            <>
              <Button variant="secondary" onClick={() => setConfirmOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button variant="primary" onClick={() => void handleConfirm()}>
                {t('notif.broadcast.confirm')}
              </Button>
            </>
          }
        >
          <div className="space-y-2 text-sm">
            <p className="text-[color:var(--stryzh-color-text-secondary)]">{t('notif.broadcast.preview')}</p>
            <div className="rounded-lg border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-surface-raised)] p-3">
              <p className="font-semibold text-[color:var(--stryzh-color-text-primary)]">{title}</p>
              <p className="mt-1 text-[color:var(--stryzh-color-text-secondary)]">{body}</p>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
